import React from "react";
import {
  useMacroCollections,
  PosIterationProvider,
} from "../MacroCollectionContext";

type MacroPosItemsRepeaterProps = {
  /** Slot for the content to repeat for each POS item - renders as a function component */
  item?: React.FC;
  /** Whether to show when there are no POS items */
  showWhenEmpty?: boolean;
  puck?: { dragRef?: React.Ref<any> };
};

/**
 * Repeater block that iterates over all POS (point of sale) items in the reservation.
 *
 * Inside the repeater, use bindings like:
 * - {{pos.Description}} - Item description
 * - {{pos.Quantity}} - Quantity
 * - {{pos.Price}} - Formatted price
 * - {{pos.ordinal}} - 1, 2, 3...
 */
const MacroPosItemsRepeaterBlock = {
  label: "POS Items Repeater",
  llmDescription: `A repeater block that iterates over all POS (point of sale) items in the reservation.

IMPORTANT - SLOT DATA MODEL:
This block uses the Puck SLOT pattern (NOT DropZones). The 'item' field is a SLOT that stores
child component data INLINE within the props, NOT in a separate zones object.

Correct PuckData structure for PosItemsRepeater:
{
  "type": "PosItemsRepeater",
  "props": {
    "id": "pos-items-repeater",
    "item": [
      { "type": "Html", "props": { "id": "html-1", "content": "<p>{{pos.Description}}: {{pos.Price}}</p>" } },
      { "type": "Divider", "props": { "id": "div-1" } }
    ]
  }
}

WRONG (do NOT use zones):
{
  "type": "PosItemsRepeater",
  "props": { "id": "pos-items-repeater" }
}
// with separate zones["pos-items-repeater:item"] - THIS IS WRONG!

Inside this repeater, you can use POS-specific bindings:
  - {{pos.Description}} - Item description (e.g., "Airport Transfer")
  - {{pos.Quantity}} - Quantity (integer)
  - {{pos.Price}} - Formatted price WITH currency (e.g., "฿2,000") - DO NOT add ฿ before this!
  - {{pos.Total}} - Alias for pos.Price (same value)
  - {{pos.ordinal}} - Position number (1, 2, 3...)
  - {{pos.count}} - Total number of POS items

CRITICAL: {{pos.Price}} and {{pos.Total}} already include ฿ symbol. Writing "฿{{pos.Price}}" will produce "฿฿2,000".

IMPORTANT: Use {{pos.Price}} or {{pos.Total}} - NOT {{pos.Amount}} (pos.Amount does not exist!)`,
  inline: true,
  fields: {
    item: { type: "slot" as const },
    showWhenEmpty: {
      type: "radio" as const,
      label: "Show when no POS items",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
  },
  defaultProps: {
    item: [] as any[],
    showWhenEmpty: false,
  },
  render: (props: MacroPosItemsRepeaterProps) => {
    const { item: Items, showWhenEmpty = false, puck } = props;
    const collections = useMacroCollections();
    const posItems = collections.posItems;

    if (!showWhenEmpty && posItems.length === 0) {
      return null;
    }

    const ref = puck?.dragRef as any;

    return (
      <div ref={ref} style={{ display: "contents" }}>
        {posItems.map((posItem, index) => (
          <PosIterationProvider
            key={index}
            posItem={posItem}
            index={index}
            count={posItems.length}
          >
            {Items && <Items />}
          </PosIterationProvider>
        ))}
      </div>
    );
  },
};

export default MacroPosItemsRepeaterBlock;
